import { FC, useEffect, useCallback, useRef, useState } from "react";
import { motion } from "framer-motion";
import { CommonForm, DefaultIcon, TextInputGroup } from "@shared/ui";
import { ApplicationAchievement } from "@entities/achivments";
import { useFormFocus } from "@shared/hooks";
import IconAward from "/svg/award.svg";
import { FormikProps } from "formik";
import { object, string } from "yup";

import styles from "./AchievementForm.module.scss";
import { LazyDatePicker, useModalScrollLock } from "@shared/utils";
import { useDecodeHtml } from "@shared/utils/decodeHTML";

export interface AchievementFormProps {
  setStatus: (status: "success" | "error" | null) => void;
  formData: {
    id: string;
    title: string;
    date: string;
  };
  onSubmit: (values: ApplicationAchievement) => Promise<void>;
  errorMessage: string | null;
}

export const achievementValidationSchema = object({
  title: string().required("Поле обязательно для заполнения"),
  date: string().required("Поле обязательно для заполнения"),
});

export const AchievementForm: FC<AchievementFormProps> = (props) => {
  const { setStatus, formData, onSubmit, errorMessage } = props;
  const formikRef = useRef<FormikProps<any> | null>(null);

  const { focusedFields, handleFieldFocus, handleFieldBlur } = useFormFocus({
    title: false,
    date: false,
  });

  useModalScrollLock(true);

  const handleFormSubmit = useCallback(
    async (values: any) => {
      await onSubmit(values);
    },
    [onSubmit]
  );

  const handleButtonClick = useCallback(async () => {
    if (formikRef.current) {
      try {
        await formikRef.current.submitForm();
      } catch (error) {
        console.error("Error submitting form:", error);
        setStatus("error");
      }
    }
  }, [setStatus]);

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  useEffect(() => {
    const tg = window.Telegram.WebApp;
    tg.MainButton.setText("Сохранить");

    if (isCalendarOpen) {
      tg.MainButton.hide();
    } else {
      tg.MainButton.show();
    }

    tg.MainButton.onClick(handleButtonClick);

    return () => {
      tg.MainButton.offClick(handleButtonClick);
      tg.MainButton.hide();
    };
  }, [handleButtonClick, isCalendarOpen]);

  return (
    <motion.div
      className={styles.achievementFormContainer}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <CommonForm
        initialValues={formData}
        onSubmit={handleFormSubmit}
        validationSchema={achievementValidationSchema}
      >
        {(formik) => {
          useEffect(() => {
            formikRef.current = formik;
          }, [formik]);

          return (
            <div className={styles.achievementFormInputs}>
              {errorMessage && (
                <div className={styles.errorMessage}>{errorMessage}</div>
              )}
              <TextInputGroup
                placeholder="Название профессионального достижения"
                label="Название"
                name="title"
                value={useDecodeHtml(formik.values.title)}
                onChange={formik.handleChange}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFieldBlur("title");
                }}
                disabled={false}
                error={formik.touched.title && formik.errors.title}
                icon={() => <DefaultIcon src={IconAward} />}
                isFocused={focusedFields.title}
                setIsFocused={(focus) => {
                  handleFieldFocus("title", focus);
                  if (!focus) {
                    handleFieldBlur("title");
                  }
                }}
                type="text"
              />

              <LazyDatePicker
                placeholder="Дата получения"
                value={formik.values.date || ""}
                onChange={(date: string) => {
                  const isoDate = date ? new Date(date).toISOString() : "";
                  formik.setFieldValue("date", isoDate);
                  formik.setFieldTouched("date", true);
                }}
                onBlur={() => {
                  formik.setFieldTouched("date", true);
                  handleFieldBlur("date");
                }}
                disabled={false}
                error={
                  formik.touched.date && !formik.values.date
                    ? "Поле обязательно для заполнения"
                    : undefined
                }
                isFocused={focusedFields.date}
                setIsFocused={(focus: boolean) => {
                  handleFieldFocus("date", focus);
                  if (!focus) {
                    handleFieldBlur("date");
                  }
                  setIsCalendarOpen(focus);
                }}
              />
            </div>
          );
        }}
      </CommonForm>
    </motion.div>
  );
};
